// src/pages/packingBatch/PackingBatchDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Package,
  ArrowLeft,
  Plus,
  Printer,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Copy,
  Check,
  User,
  Phone,
  Store,
  MapPin,
  CreditCard,
  Truck,
  Calendar,
  FileText,
  Eye,
  Search,
  Filter,
  Send,
  RotateCcw,
  List,
  Boxes,
  Wifi,
  WifiOff
} from 'lucide-react';
import {
  getPackingBatchById,
  getPackingBatchProductList,
  retryFailedOrdersWithProgress,
  addOrdersToPackingBatchWithProgress,
  searchEligibleOrders
} from '../../services/api/packingBatch';
import { exportProductListToExcel, exportProductListToPDF } from '../../utils/packingBatchProductExport';
import { dispatchPackingBatch } from '../../services/api/dispatch';
import { reversePackingBatch, reverseSingleOrder } from '../../services/api/reversal';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate } from '../../utils/dateUtils';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import FormatSelectorModal from './components/FormatSelectorModal';
import ProductListPrintModal from './components/ProductListPrintModal';
import ReversalConfirmDialog from '../../components/common/ReversalConfirmDialog/ReversalConfirmDialog';
import { getOrder } from '../../services/api/offlineSales/orders';
import OrderReceiptPrintModal from '../offlineSales/components/OrderReceiptPrintModal';
import styles from './PackingBatchDetailsPage.module.css';

const PackingBatchDetailsPage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const { hasPermission } = usePermissions();

  const canReverse = hasPermission('reverse_packing_batch');

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [showFailedOnly, setShowFailedOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Orders / Product list toggle
  const [viewMode, setViewMode] = useState('orders');
  const [productList, setProductList] = useState(null);
  const [isLoadingProductList, setIsLoadingProductList] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showAddOrdersModal, setShowAddOrdersModal] = useState(false);
  const [showFormatSelector, setShowFormatSelector] = useState(false);
  const [printOrder, setPrintOrder] = useState(null);
  const [showProductPrintModal, setShowProductPrintModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedOrdersForDispatch, setSelectedOrdersForDispatch] = useState(new Set());

  // Reversal state
  const [showReverseBatchDialog, setShowReverseBatchDialog] = useState(false);
  const [isReversingBatch, setIsReversingBatch] = useState(false);
  const [showReverseOrderDialog, setShowReverseOrderDialog] = useState(false);
  const [isReversingOrder, setIsReversingOrder] = useState(false);
  const [orderToReverse, setOrderToReverse] = useState(null); // { orderId, orderNumber }

  // Retry state
  const [isRetrying, setIsRetrying] = useState(false);
  const [showRetryProgress, setShowRetryProgress] = useState(false);
  const [retryProgressData, setRetryProgressData] = useState({
    current: 0,
    total: 0,
    orderNumber: '',
    courierAccount: '',
    status: 'processing',
    results: []
  });

  // Add orders modal state
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [isSearchingOrders, setIsSearchingOrders] = useState(false);
  const [isAddingOrders, setIsAddingOrders] = useState(false);
  const [addOrdersProgress, setAddOrdersProgress] = useState({
    current: 0,
    total: 0,
    orderNumber: '',
    courierAccount: '',
    status: 'processing'
  });
  const [showAddProgress, setShowAddProgress] = useState(false);

  useEffect(() => {
    loadBatchDetails();
  }, [batchId]);

  const loadBatchDetails = async () => {
    try {
      setLoading(true);
      const response = await getPackingBatchById(batchId);
      setBatch(response.packingBatch);
      // Batch contents may have changed — force the product list to refetch next time it's viewed
      setProductList(null);
    } catch (error) {
      console.error('Failed to load batch details:', error);
      showError(error.response?.data?.message || t('packingBatch.loadBatchError'));
    } finally {
      setLoading(false);
    }
  };

  const loadProductList = async () => {
    try {
      setIsLoadingProductList(true);
      const response = await getPackingBatchProductList(batchId);
      setProductList(response);
    } catch (error) {
      console.error('Failed to load product list:', error);
      showError(error.response?.data?.message || t('packingBatch.loadProductListError'));
    } finally {
      setIsLoadingProductList(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'products' && !productList && !isLoadingProductList) {
      loadProductList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, productList]);

  const handleCopyAirwaybill = (airwaybillNumber) => {
    navigator.clipboard.writeText(airwaybillNumber);
    setCopiedId(airwaybillNumber);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // ─── Retry with SSE progress ─────────────────────────────────────────────
  const handleRetryFailed = async () => {
    const failedOrderIds = batch.batchOrders
      .filter(bo => bo.status === 'failed')
      .map(bo => bo.orderId);

    if (failedOrderIds.length === 0) {
      showError(t('packingBatch.noFailedOrders'));
      return;
    }

    const confirmed = window.confirm(
      t('packingBatch.retryConfirm', { count: failedOrderIds.length })
    );
    if (!confirmed) return;

    setIsRetrying(true);
    setShowRetryProgress(true);
    setRetryProgressData({
      current: 0,
      total: failedOrderIds.length,
      orderNumber: '',
      courierAccount: '',
      status: 'processing',
      results: []
    });

    try {
      await retryFailedOrdersWithProgress(
        batchId,
        failedOrderIds,
        (progressUpdate) => {
          setRetryProgressData(prev => ({
            ...prev,
            current: progressUpdate.current ?? prev.current,
            total: progressUpdate.total ?? prev.total,
            orderNumber: progressUpdate.orderNumber || prev.orderNumber,
            courierAccount: progressUpdate.courierAccount || prev.courierAccount,
            status: progressUpdate.status || prev.status,
            results: [...prev.results, progressUpdate]
          }));
        },
        (result) => {
          setShowRetryProgress(false);
          setIsRetrying(false);
          const retried = result.retriedOrders?.length ?? 0;
          const stillFailed = result.stillFailedOrders?.length ?? 0;
          if (stillFailed === 0) {
            success(t('packingBatch.retryCompleted'));
          } else {
            showError(
              t('packingBatch.retryPartial', { retried, failed: stillFailed }) ||
              `Retry complete: ${retried} succeeded, ${stillFailed} still failed.`
            );
          }
          loadBatchDetails();
        },
        (err) => {
          setShowRetryProgress(false);
          setIsRetrying(false);
          showError(err.message || t('packingBatch.retryError'));
          loadBatchDetails();
        }
      );
    } catch (error) {
      console.error('Retry SSE error:', error);
      setShowRetryProgress(false);
      setIsRetrying(false);
      showError(error.message || t('packingBatch.retryError'));
    }
  };

  // ─── Reverse entire batch ─────────────────────────────────────────────────
  const handleConfirmReverseBatch = async (targetStatusKey, orderStatusReasonKey) => {
    setIsReversingBatch(true);
    try {
      const result = await reversePackingBatch(batchId, targetStatusKey, orderStatusReasonKey);
      setShowReverseBatchDialog(false);
      const reversed = result.reversedOrders?.length ?? 0;
      const failed = result.failedOrders?.length ?? 0;
      const targetLabel = targetStatusKey === 'cancelled' ? 'Cancelled' : 'Confirmed';
      if (failed === 0) {
        success(`Batch ${result.batchNumber} reversed successfully. ${reversed} order(s) returned to ${targetLabel}.`);
      } else {
        showError(`Batch reversal partially completed. ${reversed} reversed, ${failed} failed.`);
      }
      loadBatchDetails();
    } catch (error) {
      console.error('Reverse batch error:', error);
      showError(error.response?.data?.message || 'Failed to reverse packing batch');
    } finally {
      setIsReversingBatch(false);
    }
  };

  // ─── Reverse single order ─────────────────────────────────────────────────
  const handleClickReverseOrder = (batchOrder) => {
    setOrderToReverse({
      orderId: batchOrder.orderId,
      orderNumber: batchOrder.EcomOrder.orderNumber
    });
    setShowReverseOrderDialog(true);
  };

  const handleConfirmReverseOrder = async (targetStatusKey, orderStatusReasonKey) => {
    if (!orderToReverse) return;
    setIsReversingOrder(true);
    try {
      const result = await reverseSingleOrder(orderToReverse.orderId, targetStatusKey, orderStatusReasonKey);
      setShowReverseOrderDialog(false);
      setOrderToReverse(null);
      const targetLabel = targetStatusKey === 'cancelled' ? 'Cancelled' : 'Confirmed';
      success(`Order ${result.orderNumber} reversed successfully and returned to ${targetLabel}.`);
      loadBatchDetails();
    } catch (error) {
      console.error('Reverse order error:', error);
      showError(error.response?.data?.message || 'Failed to reverse order');
    } finally {
      setIsReversingOrder(false);
    }
  };

  const handleSearchEligibleOrders = async () => {
    if (!batch) return;
    try {
      setIsSearchingOrders(true);
      const params = {
        warehouseId: batch.warehouseId,
        businessCourierId: batch.businessCourierId,
        orderStatusKeys: 'confirmed',
        limit: 100
      };
      const response = await searchEligibleOrders(params);
      setEligibleOrders(response.orders || []);
    } catch (error) {
      console.error('Failed to search orders:', error);
      showError(t('packingBatch.searchOrdersError'));
    } finally {
      setIsSearchingOrders(false);
    }
  };

  const handleAddOrders = async () => {
    if (selectedOrderIds.length === 0) {
      showError(t('packingBatch.selectAtLeastOneOrder'));
      return;
    }

    try {
      setIsAddingOrders(true);
      setShowAddProgress(true);
      setAddOrdersProgress({ current: 0, total: selectedOrderIds.length, orderNumber: '', courierAccount: '', status: 'processing' });

      await addOrdersToPackingBatchWithProgress(
        batchId,
        selectedOrderIds,
        (progressUpdate) => {
          setAddOrdersProgress(prev => ({
            ...prev,
            current: progressUpdate.current ?? prev.current,
            total: progressUpdate.total ?? prev.total,
            orderNumber: progressUpdate.orderNumber || prev.orderNumber,
            courierAccount: progressUpdate.courierAccount || prev.courierAccount,
            status: progressUpdate.status || prev.status
          }));
        },
        (result) => {
          setShowAddProgress(false);
          setShowAddOrdersModal(false);
          success(t('packingBatch.ordersAddedSuccess', { count: result.addedOrders.length }));
          setSelectedOrderIds([]);
          loadBatchDetails();
          setIsAddingOrders(false);
        },
        (error) => {
          setShowAddProgress(false);
          showError(error.message || t('packingBatch.addOrdersError'));
          setIsAddingOrders(false);
        }
      );
    } catch (error) {
      console.error('Failed to add orders:', error);
      showError(t('packingBatch.addOrdersError'));
      setShowAddProgress(false);
      setIsAddingOrders(false);
    }
  };

  const handleExport = () => {
    navigate(`/packing-batches/courier-upload-format?packingBatchId=${batchId}`);
  };

  const handleDispatchBatch = async () => {
    const successfulOrders = batch.batchOrders.filter(bo => bo.status === 'success');
    if (successfulOrders.length === 0) {
      showError(t('dispatch.noOrdersToDispatch'));
      return;
    }
    setShowDispatchModal(true);
    setSelectedOrdersForDispatch(new Set(successfulOrders.map(bo => bo.orderId)));
  };

  const handleConfirmDispatch = async () => {
    try {
      setIsDispatching(true);
      const successfulOrders = batch.batchOrders.filter(bo => bo.status === 'success').map(bo => bo.orderId);
      const excludeOrderIds = successfulOrders.filter(id => !selectedOrdersForDispatch.has(id));

      const result = await dispatchPackingBatch(batchId, { excludeOrderIds, dispatchedDate: dispatchDate });
      success(t('dispatch.batchDispatchSuccess', { count: result.dispatchedOrders.length }));
      setShowDispatchModal(false);
      await loadBatchDetails();
    } catch (error) {
      console.error('Failed to dispatch batch:', error);
      showError(error.response?.data?.message || t('dispatch.dispatchFailed'));
    } finally {
      setIsDispatching(false);
    }
  };

  const handleToggleOrderSelection = (orderId) => {
    setSelectedOrdersForDispatch(prev => {
      const newSet = new Set(prev);
      newSet.has(orderId) ? newSet.delete(orderId) : newSet.add(orderId);
      return newSet;
    });
  };

  const handleSelectAllDispatch = () => {
    setSelectedOrdersForDispatch(new Set(batch.batchOrders.filter(bo => bo.status === 'success').map(bo => bo.orderId)));
  };

  const handleDeselectAllDispatch = () => {
    setSelectedOrdersForDispatch(new Set());
  };

  const handlePrintAirwaybills = () => {
    navigate(`/packing-batches/waybill-print?packingBatchId=${batchId}`);
  };

  // ─── Product list print / export ─────────────────────────────────────────
  const handlePrintProductList = () => {
    if (!productList) return;
    setShowProductPrintModal(true);
  };

  const handleExportProductListExcel = () => {
    if (!productList) return;
    const result = exportProductListToExcel(productList.products, batch.batchNumber);
    if (result.success) success(result.message);
    else showError(result.message);
  };

  const handleExportProductListPDF = () => {
    if (!productList) return;
    const result = exportProductListToPDF(productList.products, batch.batchNumber);
    if (result.success) success(result.message);
    else showError(result.message);
  };

  const handlePrintOrder = async (batchOrder) => {
    try {
      const result = await getOrder(batchOrder.EcomOrder.id);
      setPrintOrder(result.order || result);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to load order for printing');
    }
  };

  // ─── Build order items description string ────────────────────────────────
  const buildItemsDescription = (orderItems = []) => {
    if (!orderItems || orderItems.length === 0) return '—';
    return orderItems
      .map(item => {
        const name = item.Product?.name || 'Unknown';
        const qty = parseFloat(item.quantity) % 1 === 0
          ? parseInt(item.quantity)
          : parseFloat(item.quantity);
        return `${name} × ${qty}`;
      })
      .join(', ');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':    return <CheckCircle size={16} className={styles.successIcon} />;
      case 'failed':     return <XCircle size={16} className={styles.errorIcon} />;
      case 'processing': return <Loader2 size={16} className={`${styles.processingIcon} ${styles.spinning}`} />;
      case 'dispatched': return <Send size={16} className={styles.dispatchedIcon} />;
      case 'reversed':   return <RotateCcw size={16} className={styles.reversedIcon} />;
      default:           return <AlertCircle size={16} className={styles.warningIcon} />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':        return styles.statusCompleted;
      case 'in_progress':      return styles.statusInProgress;
      case 'partially_failed': return styles.statusPartiallyFailed;
      case 'failed':           return styles.statusFailed;
      default:                 return styles.statusDefault;
    }
  };

  const getBatchOrderStatusClass = (status) => {
    switch (status) {
      case 'success':    return styles.orderStatusSuccess;
      case 'failed':     return styles.orderStatusFailed;
      case 'processing': return styles.orderStatusProcessing;
      case 'dispatched': return styles.orderStatusDispatched;
      case 'reversed':   return styles.orderStatusReversed;
      default:           return styles.orderStatusDefault;
    }
  };

  const filteredOrders = batch?.batchOrders?.filter(batchOrder => {
    const matchesSearch = !searchTerm ||
      batchOrder.EcomOrder.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      batchOrder.EcomOrder.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      batchOrder.airwaybillNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || batchOrder.status === statusFilter;
    const matchesFailedFilter = !showFailedOnly || batchOrder.status === 'failed';
    return matchesSearch && matchesStatus && matchesFailedFilter;
  }) || [];

  const filteredProducts = (productList?.products || []).filter(product => {
    if (!productSearchTerm) return true;
    const term = productSearchTerm.toLowerCase();
    return (
      product.displayName.toLowerCase().includes(term) ||
      (product.sku || '').toLowerCase().includes(term)
    );
  });

  const summaryStats = batch ? {
    successful: batch.batchOrders.filter(bo => bo.status === 'success').length,
    failed:     batch.batchOrders.filter(bo => bo.status === 'failed').length,
    processing: batch.batchOrders.filter(bo => bo.status === 'processing').length,
    dispatched: batch.batchOrders.filter(bo => bo.status === 'dispatched').length,
    reversed:   batch.batchOrders.filter(bo => bo.status === 'reversed').length,
    totalValue: batch.batchOrders
      .filter(bo => bo.status === 'success')
      .reduce((sum, bo) => sum + parseFloat(bo.EcomOrder.grandTotal || 0), 0)
  } : null;

  const retrySessionSucceeded = retryProgressData.results.filter(r => r.status === 'success').length;
  const retrySessionFailed    = retryProgressData.results.filter(r => r.status === 'failed').length;

  // Determine if any orders are reversible
  const reversibleOrdersCount = batch?.batchOrders?.filter(
    bo => bo.status === 'success' || bo.status === 'dispatched'
  ).length ?? 0;

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 size={48} className={styles.spinning} />
        <p>{t('packingBatch.loadingBatch')}</p>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className={styles.errorContainer}>
        <AlertCircle size={48} />
        <h3>{t('packingBatch.batchNotFound')}</h3>
        <button onClick={() => navigate('/packing-batches')} className={styles.backButton}>
          <ArrowLeft size={18} />
          {t('packingBatch.backToBatches')}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.detailsPage}>
      <PageHeader
        title={t('packingBatch.batchNumberTitle', { number: batch.batchNumber })}
        description={
          <div className={`${styles.statusBadge} ${getStatusBadgeClass(batch.status)}`}>
            {t(`packingBatch.status${batch.status.charAt(0).toUpperCase() + batch.status.slice(1).replace('_', '')}`)}
          </div>
        }
        icon={Package}
        actions={
          <>
            <button onClick={() => navigate(batch.channel === 'offline' ? '/offline-sales/packing' : '/packing-batches')} className={styles.actionButton}>
              <ArrowLeft size={18} />
              {t('common.back')}
            </button>
            <button
              onClick={() => setShowAddOrdersModal(true)}
              className={styles.actionButton}
              disabled={batch.status === 'completed'}
            >
              <Plus size={18} />
              {t('packingBatch.addOrders')}
            </button>
            <button onClick={handlePrintAirwaybills} className={styles.actionButton}>
              <Printer size={18} />
              {t('packingBatch.printAirwaybills')}
            </button>
            <button onClick={handleExport} className={styles.actionButton}>
              <Download size={18} />
              {t('common.export')}
            </button>
            <button
              onClick={handleDispatchBatch}
              className={styles.actionButton}
              disabled={summaryStats.successful === 0}
            >
              <Send size={18} />
              {t('dispatch.dispatchBatch')}
            </button>
            {canReverse && reversibleOrdersCount > 0 && (
              <button
                onClick={() => setShowReverseBatchDialog(true)}
                className={`${styles.actionButton} ${styles.reverseButton}`}
                disabled={isReversingBatch}
              >
                <RotateCcw size={18} />
                Reverse Batch
              </button>
            )}
          </>
        }
      />

      {/* Batch Info Cards */}
      <div className={styles.infoCards}>
        <div className={styles.infoCard}>
          <div className={styles.cardIcon}><Calendar size={20} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('packingBatch.created')}</span>
            <span className={styles.cardValue}>{formatDate(batch.createdAt)}</span>
            <span className={styles.cardSubtext}>{t('packingBatch.by')} {batch.createdBy?.name || t('common.unknown')}</span>
          </div>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.cardIcon}><MapPin size={20} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('packingBatch.warehouse')}</span>
            <span className={styles.cardValue}>{batch.Warehouse?.name || t('common.na')}</span>
            <span className={styles.cardSubtext}>{batch.Warehouse?.address || ''}</span>
          </div>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.cardIcon}><Truck size={20} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('packingBatch.courierAccount')}</span>
            <span className={styles.cardValue}>{batch.courierAccount?.accountName || t('common.na')}</span>
            <span className={styles.cardSubtext}>{batch.CourierCompany?.name || ''}</span>
          </div>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.cardIcon}><Package size={20} /></div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('packingBatch.totalOrders')}</span>
            <span className={styles.cardValue}>{batch.totalOrders}</span>
          </div>
        </div>
        <div className={`${styles.infoCard} ${batch.isPrinted ? styles.printedInfoCard : styles.notPrintedInfoCard}`}>
          <div className={styles.cardIcon}>
            <Printer size={20} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('packingBatch.printed')}</span>
            <span className={`${styles.printStatusValue} ${batch.isPrinted ? styles.printStatusPrinted : styles.printStatusNotPrinted}`}>
              {batch.isPrinted ? t('packingBatch.printed') : t('packingBatch.notPrinted')}
            </span>
            {batch.isPrinted && batch.printedAt && (
              <span className={styles.cardSubtext}>{formatDate(batch.printedAt)}</span>
            )}
          </div>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.cardIcon}>
            {batch.channel === 'offline' ? <WifiOff size={20} /> : <Wifi size={20} />}
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('packingBatch.channel')}</span>
            <span className={styles.cardValue}>
              {batch.channel === 'offline' ? t('packingBatch.channelOffline') : t('packingBatch.channelOnline')}
            </span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={styles.summaryCards}>
        {summaryStats.failed > 0 && (
          <div className={`${styles.summaryCard} ${styles.errorCard}`}>
            <div className={styles.summaryIcon}><XCircle size={24} /></div>
            <div className={styles.summaryContent}>
              <h3>{summaryStats.failed}</h3>
              <p>{t('packingBatch.failedOrders')}</p>
              <button onClick={handleRetryFailed} disabled={isRetrying} className={styles.retryButton}>
                {isRetrying ? (
                  <><Loader2 size={14} className={styles.spinning} />{t('packingBatch.retrying')}</>
                ) : (
                  <><RefreshCw size={14} />{t('packingBatch.retryAll')}</>
                )}
              </button>
            </div>
          </div>
        )}
        {summaryStats.processing > 0 && (
          <div className={`${styles.summaryCard} ${styles.processingCard}`}>
            <div className={styles.summaryIcon}><Loader2 size={24} className={styles.spinning} /></div>
            <div className={styles.summaryContent}>
              <h3>{summaryStats.processing}</h3>
              <p>{t('packingBatch.processing')}</p>
            </div>
          </div>
        )}
        {summaryStats.dispatched > 0 && (
          <div className={`${styles.summaryCard} ${styles.dispatchedCard}`}>
            <div className={styles.summaryIcon}><Send size={24} /></div>
            <div className={styles.summaryContent}>
              <h3>{summaryStats.dispatched}</h3>
              <p>{t('dispatch.dispatchedOrders')}</p>
            </div>
          </div>
        )}
        {summaryStats.reversed > 0 && (
          <div className={`${styles.summaryCard} ${styles.reversedCard}`}>
            <div className={styles.summaryIcon}><RotateCcw size={24} /></div>
            <div className={styles.summaryContent}>
              <h3>{summaryStats.reversed}</h3>
              <p>Reversed</p>
            </div>
          </div>
        )}
      </div>

      {batch.notes && (
        <div className={styles.notesSection}>
          <FileText size={18} />
          <div className={styles.notesContent}>
            <strong>{t('packingBatch.notes')}:</strong> {batch.notes}
          </div>
        </div>
      )}

      {/* Orders / Product List Table */}
      <div className={styles.ordersSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeaderTitle}>
            <h2>
              {viewMode === 'orders'
                ? t('packingBatch.ordersInBatch', { count: filteredOrders.length })
                : t('packingBatch.productListTitle', { count: filteredProducts.length })}
            </h2>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewToggleButton} ${viewMode === 'orders' ? styles.viewToggleActive : ''}`}
                onClick={() => setViewMode('orders')}
              >
                <List size={16} />
                {t('packingBatch.viewOrdersTab')}
              </button>
              <button
                className={`${styles.viewToggleButton} ${viewMode === 'products' ? styles.viewToggleActive : ''}`}
                onClick={() => setViewMode('products')}
              >
                <Boxes size={16} />
                {t('packingBatch.viewProductsTab')}
              </button>
            </div>
          </div>

          {viewMode === 'orders' ? (
            <div className={styles.tableControls}>
              <div className={styles.searchBox}>
                <Search size={18} />
                <input
                  type="text"
                  placeholder={t('packingBatch.searchOrdersPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={styles.statusSelect}
              >
                <option value="all">{t('packingBatch.allStatus')}</option>
                <option value="success">{t('packingBatch.success')}</option>
                <option value="failed">{t('packingBatch.failed')}</option>
                <option value="processing">{t('packingBatch.processing')}</option>
                <option value="dispatched">{t('dispatch.dispatched')}</option>
                <option value="reversed">Reversed</option>
              </select>
              {summaryStats.failed > 0 && (
                <button
                  onClick={() => setShowFailedOnly(!showFailedOnly)}
                  className={`${styles.filterButton} ${showFailedOnly ? styles.active : ''}`}
                >
                  <Filter size={16} />
                  {showFailedOnly ? t('packingBatch.showAll') : t('packingBatch.failedOnly')}
                </button>
              )}
            </div>
          ) : (
            <div className={styles.tableControls}>
              <div className={styles.searchBox}>
                <Search size={18} />
                <input
                  type="text"
                  placeholder={t('packingBatch.searchProductsPlaceholder')}
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                />
              </div>
              <button
                onClick={handlePrintProductList}
                className={styles.filterButton}
                disabled={!productList}
              >
                <Printer size={16} />
                {t('packingBatch.printProductList')}
              </button>
              <button
                onClick={handleExportProductListExcel}
                className={styles.filterButton}
                disabled={!productList}
              >
                <Download size={16} />
                {t('packingBatch.exportExcel')}
              </button>
              <button
                onClick={handleExportProductListPDF}
                className={styles.filterButton}
                disabled={!productList}
              >
                <Download size={16} />
                {t('packingBatch.exportPdf')}
              </button>
            </div>
          )}
        </div>

        {viewMode === 'orders' ? (
          <>
            <div className={styles.tableContainer}>
              <table className={styles.ordersTable}>
                <thead>
                  <tr>
                    <th>{t('packingBatch.orderNumber')}</th>
                    <th>{t('packingBatch.customerName')}</th>
                    <th>{t('packingBatch.phoneNumber')}</th>
                    <th>{t('packingBatch.city')}</th>
                    <th>{t('packingBatch.shop')}</th>
                    <th>{t('packingBatch.items')}</th>
                    <th>{t('packingBatch.grandTotal')}</th>
                    <th>{t('packingBatch.airwaybillNumber')}</th>
                    <th>{t('packingBatch.status')}</th>
                    <th className={styles.actionsColumnSticky}>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((batchOrder) => {
                    const isReversible = canReverse &&
                      (batchOrder.status === 'success' || batchOrder.status === 'dispatched');
                    return (
                      <tr key={batchOrder.id}>
                        <td>
                          <div className={styles.orderNumber}>
                            #{batchOrder.EcomOrder.orderNumber}
                          </div>
                        </td>
                        <td>
                          <div className={styles.customerInfo}>
                            <User size={12} />{batchOrder.EcomOrder.customerName}
                          </div>
                        </td>
                        <td>
                          <div className={styles.phoneInfo}>
                            <Phone size={12} />
                            {batchOrder.EcomOrder.phoneNumbers?.[0]?.phoneNumber || t('common.na')}
                          </div>
                        </td>
                        <td>
                          <div className={styles.cityInfo}>
                            <MapPin size={12} />{batchOrder.EcomOrder.City?.name || t('common.na')}
                          </div>
                        </td>
                        <td>
                          <div className={styles.shopBadge}>
                            <Store size={12} />{batchOrder.EcomOrder.Shop?.name || t('common.na')}
                          </div>
                        </td>
                        <td>
                          <div
                            className={styles.itemsDescription}
                            title={buildItemsDescription(batchOrder.EcomOrder.orderItems)}
                          >
                            {buildItemsDescription(batchOrder.EcomOrder.orderItems)}
                          </div>
                        </td>
                        <td>
                          <div className={styles.totalAmount}>
                            {formatCurrency(batchOrder.EcomOrder.grandTotal)}
                          </div>
                        </td>
                        <td>
                          {batchOrder.airwaybillNumber ? (
                            <div className={styles.airwaybillCell}>
                              <span className={styles.airwaybillNumber}>
                                {batchOrder.airwaybillNumber}
                              </span>
                              <button
                                onClick={() => handleCopyAirwaybill(batchOrder.airwaybillNumber)}
                                className={styles.copyButton}
                                title={t('packingBatch.copyAirwaybill')}
                              >
                                {copiedId === batchOrder.airwaybillNumber
                                  ? <Check size={14} className={styles.checkIcon} />
                                  : <Copy size={14} />}
                              </button>
                            </div>
                          ) : (
                            <span className={styles.noAirwaybill}>-</span>
                          )}
                        </td>
                        <td>
                          <div className={`${styles.orderStatus} ${getBatchOrderStatusClass(batchOrder.status)}`}>
                            {getStatusIcon(batchOrder.status)}
                            {batchOrder.status}
                          </div>
                          {batchOrder.errorMessage && (
                            <div className={styles.errorMessage} title={batchOrder.errorMessage}>
                              {batchOrder.errorMessage}
                            </div>
                          )}
                        </td>
                        <td className={styles.actionsColumnSticky}>
                          <div className={styles.rowActions}>
                            <button
                              className={styles.viewButton}
                              title={t('packingBatch.viewOrder')}
                              onClick={() => navigate(`/orders/${batchOrder.EcomOrder.id}`)}
                            >
                              <Eye size={16} />
                            </button>
                            {batch.channel === 'offline' && (
                          <button
                            className={styles.viewButton}
                            title={t('offlineSales.printReceiptAction')}
                            onClick={() => handlePrintOrder(batchOrder)}
                          >
                            <Printer size={16} />
                          </button>
                        )}
                        {isReversible && (
                              <button
                                className={styles.reverseOrderButton}
                                title="Reverse this order"
                                onClick={() => handleClickReverseOrder(batchOrder)}
                              >
                                <RotateCcw size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredOrders.length === 0 && (
              <div className={styles.emptyState}>
                <Package size={48} />
                <h3>{t('packingBatch.noOrdersFound')}</h3>
                <p>{t('packingBatch.noMatchingFilters')}</p>
              </div>
            )}
          </>
        ) : isLoadingProductList ? (
          <div className={styles.loadingContainer}>
            <Loader2 size={32} className={styles.spinning} />
            <p>{t('packingBatch.loadingProductList')}</p>
          </div>
        ) : (
          <>
            <div className={styles.tableContainer}>
              <table className={`${styles.ordersTable} ${styles.productsTable}`}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('packingBatch.productColumn')}</th>
                    <th>{t('packingBatch.skuColumn')}</th>
                    <th>{t('packingBatch.totalQuantityColumn')}</th>
                    <th>{t('packingBatch.orderCountColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product, index) => (
                    <tr key={`${product.productId}-${product.variationId || 'base'}`}>
                      <td>{index + 1}</td>
                      <td>
                        <div className={styles.orderNumber}>{product.displayName}</div>
                      </td>
                      <td>{product.sku || t('common.na')}</td>
                      <td>
                        <div className={styles.totalAmount}>{product.totalQuantity}</div>
                      </td>
                      <td>{product.ordersCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredProducts.length === 0 && (
              <div className={styles.emptyState}>
                <Package size={48} />
                <h3>{t('packingBatch.noProductsFound')}</h3>
                <p>{t('packingBatch.noProductsFoundDescription')}</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Reversal Dialogs ───────────────────────────────────────────────── */}
      <ReversalConfirmDialog
        isOpen={showReverseBatchDialog}
        onClose={() => setShowReverseBatchDialog(false)}
        onConfirm={handleConfirmReverseBatch}
        title="Reverse Entire Packing Batch"
        description={`You are about to reverse batch ${batch.batchNumber}. All ${reversibleOrdersCount} reversible order(s) will be removed from this batch and their airwaybill numbers cleared.`}
        isLoading={isReversingBatch}
      />

      <ReversalConfirmDialog
        isOpen={showReverseOrderDialog}
        onClose={() => { setShowReverseOrderDialog(false); setOrderToReverse(null); }}
        onConfirm={handleConfirmReverseOrder}
        title={`Reverse Order #${orderToReverse?.orderNumber || ''}`}
        description={`Order ${orderToReverse?.orderNumber} will be removed from this batch and its airwaybill number cleared.`}
        isLoading={isReversingOrder}
      />

      {/* ─── Retry Progress Modal ─────────────────────────────────────────── */}
      {showRetryProgress && (
        <div className={styles.modalOverlay}>
          <div className={styles.progressModal}>
            <div className={styles.progressHeader}>
              <h3>{t('packingBatch.retrying') || 'Retrying Failed Orders'}</h3>
              <div className={styles.progressStats}>
                {retryProgressData.current} / {retryProgressData.total} orders
                {retryProgressData.courierAccount && <span> • {retryProgressData.courierAccount}</span>}
              </div>
              <div className={styles.progressTally}>
                <span className={styles.tallySuccess}>
                  <CheckCircle size={13} /> {retrySessionSucceeded} succeeded
                </span>
                {retrySessionFailed > 0 && (
                  <span className={styles.tallyFailed}>
                    <XCircle size={13} /> {retrySessionFailed} failed
                  </span>
                )}
              </div>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{
                  width: `${retryProgressData.total > 0
                    ? (retryProgressData.current / retryProgressData.total) * 100
                    : 0}%`
                }}
              />
            </div>
            <div className={styles.progressList}>
              {retryProgressData.results.slice(-5).reverse().map((result, index) => (
                <div key={index} className={styles.progressItem}>
                  <div className={styles.progressOrder}>{result.orderNumber}</div>
                  <div className={`${styles.progressStatus} ${styles[result.status] || ''}`}>
                    {result.status === 'success'
                      ? <CheckCircle size={14} />
                      : result.status === 'failed'
                        ? <XCircle size={14} />
                        : <Loader2 size={14} className={styles.spinning} />}
                    {result.status}
                    {result.error && (
                      <span className={styles.progressError} title={result.error}>
                        : {result.error.length > 40 ? result.error.slice(0, 40) + '…' : result.error}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Orders Modal */}
      {showAddOrdersModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.addOrdersModal}>
            <div className={styles.modalHeader}>
              <h3>{t('packingBatch.addOrdersToBatch')}</h3>
              <button onClick={() => setShowAddOrdersModal(false)} className={styles.closeButton}>×</button>
            </div>
            <div className={styles.modalBody}>
              {eligibleOrders.length === 0 ? (
                <div className={styles.searchPrompt}>
                  <Package size={48} />
                  <p>{t('packingBatch.searchEligiblePrompt')}</p>
                  <button
                    onClick={handleSearchEligibleOrders}
                    disabled={isSearchingOrders}
                    className={styles.searchButton}
                  >
                    {isSearchingOrders ? (
                      <><Loader2 size={18} className={styles.spinning} />{t('packingBatch.searching')}</>
                    ) : (
                      <><Search size={18} />{t('packingBatch.searchOrders')}</>
                    )}
                  </button>
                </div>
              ) : (
                <>
                  <div className={styles.ordersList}>
                    {eligibleOrders.map((order) => (
                      <label key={order.id} className={styles.orderItem}>
                        <input
                          type="checkbox"
                          checked={selectedOrderIds.includes(order.id)}
                          onChange={() => setSelectedOrderIds(prev =>
                            prev.includes(order.id)
                              ? prev.filter(id => id !== order.id)
                              : [...prev, order.id]
                          )}
                        />
                        <div className={styles.orderInfo}>
                          <strong>#{order.orderNumber}</strong>
                          <span>{order.customerName}</span>
                          <span>{formatCurrency(order.grandTotal)}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className={styles.modalFooter}>
                    <button
                      onClick={() => { setShowAddOrdersModal(false); setSelectedOrderIds([]); setEligibleOrders([]); }}
                      className={styles.cancelButton}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleAddOrders}
                      disabled={selectedOrderIds.length === 0 || isAddingOrders}
                      className={styles.addButton}
                    >
                      {isAddingOrders ? (
                        <><Loader2 size={18} className={styles.spinning} />{t('packingBatch.adding')}</>
                      ) : (
                        <><Plus size={18} />{t('packingBatch.addOrdersCount', { count: selectedOrderIds.length })}</>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Orders Progress Modal */}
      {showAddProgress && (
        <div className={styles.modalOverlay}>
          <div className={styles.progressModal}>
            <div className={styles.progressHeader}>
              <h3>{t('packingBatch.addingOrders')}</h3>
              <div className={styles.progressStats}>
                {t('packingBatch.processingOrder')} {addOrdersProgress.current} {t('packingBatch.of')} {addOrdersProgress.total}
                {addOrdersProgress.courierAccount && <span> • {addOrdersProgress.courierAccount}</span>}
              </div>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${addOrdersProgress.total > 0 ? (addOrdersProgress.current / addOrdersProgress.total) * 100 : 0}%` }}
              />
            </div>
            <div className={styles.currentOrder}>
              {addOrdersProgress.orderNumber && (
                <p>{t('packingBatch.processing')}: {addOrdersProgress.orderNumber}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dispatch Modal */}
      {showDispatchModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.dispatchModal}>
            <div className={styles.modalHeader}>
              <h3>{t('dispatch.dispatchBatch')}</h3>
              <button onClick={() => setShowDispatchModal(false)} className={styles.closeButton}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.dispatchDateSection}>
                <label>{t('dispatch.dispatchDate')}</label>
                <input
                  type="date"
                  value={dispatchDate}
                  onChange={(e) => setDispatchDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className={styles.dateInput}
                />
              </div>
              <div className={styles.orderSelectionSection}>
                <div className={styles.selectionHeader}>
                  <h4>{t('dispatch.selectOrdersToDispatch')}</h4>
                  <div className={styles.selectionActions}>
                    <button onClick={handleSelectAllDispatch} className={styles.linkButton}>
                      {t('common.selectAll')}
                    </button>
                    <button onClick={handleDeselectAllDispatch} className={styles.linkButton}>
                      {t('common.deselectAll')}
                    </button>
                  </div>
                </div>
                <div className={styles.ordersList}>
                  {batch.batchOrders.filter(bo => bo.status === 'success').map((batchOrder) => (
                    <label key={batchOrder.orderId} className={styles.orderItem}>
                      <input
                        type="checkbox"
                        checked={selectedOrdersForDispatch.has(batchOrder.orderId)}
                        onChange={() => handleToggleOrderSelection(batchOrder.orderId)}
                      />
                      <div className={styles.orderInfo}>
                        <strong>#{batchOrder.EcomOrder.orderNumber}</strong>
                        <span>{batchOrder.EcomOrder.customerName}</span>
                        <span>{batchOrder.airwaybillNumber}</span>
                      </div>
                    </label>
                  ))}
                </div>
                <div className={styles.selectionSummary}>
                  {t('dispatch.selectedOrdersCount', {
                    selected: selectedOrdersForDispatch.size,
                    total: batch.batchOrders.filter(bo => bo.status === 'success').length
                  })}
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button onClick={() => setShowDispatchModal(false)} className={styles.cancelButton}>
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmDispatch}
                disabled={selectedOrdersForDispatch.size === 0 || isDispatching}
                className={styles.confirmButton}
              >
                {isDispatching ? (
                  <><Loader2 size={18} className={styles.spinning} />{t('dispatch.dispatching')}</>
                ) : (
                  <><Send size={18} />{t('dispatch.confirmDispatch', { count: selectedOrdersForDispatch.size })}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <FormatSelectorModal
        isOpen={showFormatSelector}
        onClose={() => setShowFormatSelector(false)}
        batchId={batchId}
      />

      {showProductPrintModal && productList && (
        <ProductListPrintModal
          products={productList.products}
          batchNumber={batch.batchNumber}
          onClose={() => setShowProductPrintModal(false)}
          onError={showError}
        />
      )}

      {printOrder && (
        <OrderReceiptPrintModal order={printOrder} onClose={() => setPrintOrder(null)} />
      )}
    </div>
  );
};

export default PackingBatchDetailsPage;